import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { consumeReturnUrl } from './authHandoffReturn';
import { useAuth } from './AuthProvider';

export default function OAuthCallback() {
  const navigate = useNavigate();
  const { applyTokens } = useAuth();

  useEffect(() => {
    // Tokens come in URL fragment (from OAuth or SSO callback)
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    // Check for errors in query string (SSO/OAuth errors)
    const queryParams = new URLSearchParams(window.location.search);
    const error = queryParams.get('error');

    // The backend echoes the desktop-handoff returnUrl as a query param on the
    // success redirect. Prefer sessionStorage (set by stashReturnUrlIfAuthHandoff
    // before we left), but fall back to this query value — the in-app browser
    // (ASWebAuthenticationSession) can drop sessionStorage across the cross-origin
    // OAuth hop, and without the fallback the cadence:// handoff dead-ends in a
    // web session. Only accept a same-origin relative path (no open-redirect).
    const queryReturnRaw = queryParams.get('returnUrl');
    const queryReturnUrl =
      queryReturnRaw && queryReturnRaw.startsWith('/') && !queryReturnRaw.startsWith('//')
        ? queryReturnRaw
        : null;
    const resolveReturnUrl = () => consumeReturnUrl() || queryReturnUrl;

    if (error) {
      // Redirect to login with error message
      sessionStorage.setItem('authError', error);
      window.location.href = '/';
      return;
    }

    if (accessToken && refreshToken) {
      // Parse the JWT to pull the identity claims into the flat shape
      // `applyTokens` (AuthProvider.applyTokenResponse) expects. The backend
      // signs these with MapInboundClaims=false, so the access token carries
      // the long WS-* claim URIs (NameIdentifier / Name / Role) plus our short
      // `tid` / `is_tenant_admin`.
      let claims: Record<string, any> = {};
      try {
        const base64 = accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        claims = JSON.parse(atob(base64));
      } catch {
        // Fall through with empty claims — applyTokens still stores the tokens
        // and the bootstrap /auth/me fills in the user.
      }

      // Apply IN MEMORY via the same path password / passkey login use, rather
      // than writing sessionStorage and doing a full reload. The old write +
      // `window.location.href='/'` reload seam silently dropped the session on
      // the social-login path (the reload re-read raced AuthGate), so SSO
      // bounced users straight back to the login screen. applyTokens sets live
      // state; a soft navigate then lands them in the app.
      applyTokens({
        accessToken,
        refreshToken,
        userId: claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || '',
        displayName: claims['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] || '',
        tenantId: claims['tid'] || '',
        role: claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || '',
        isTenantAdmin: claims['is_tenant_admin'] === 'true',
      });

      // Clear the per-day "brief dismissed" flag so Executives land on /brief
      // on every fresh sign-in. (applyTokens already does this on a session
      // transition, but keep it explicit for the SSO path.)
      try { localStorage.removeItem('cadence_brief_dismissed_date'); } catch { /* private browsing */ }

      // Drop the token fragment from the URL so a refresh can't replay it.
      try { window.history.replaceState(null, '', window.location.pathname); } catch { /* no-op */ }

      // Admin signing in via SSO without an enrolled factor: the token carries
      // mfa_pending, and the backend 403s every protected endpoint. Route
      // straight to /mfa-setup (mirrors the password-login path in
      // AuthProvider). Without this they'd land on '/', fire a doomed request,
      // and only reach /mfa-setup via the 403 interceptor — uglier, and the
      // MFA banner never primes. Skipped on auth-handoff flows (handled below).
      const mfaPending = claims['mfa_pending'] === 'true';

      // Honour a stashed auth-handoff return URL (/oauth/desktop, /oauth/
      // authorize) so those flows complete; otherwise land in the app. Those
      // handoff targets are full reloads by design (they hand the session to a
      // native app / consent page); the normal path is a soft navigate.
      const returnUrl = resolveReturnUrl();
      if (returnUrl) {
        window.location.href = returnUrl;
      } else if (mfaPending) {
        sessionStorage.setItem('mfaRequired', 'true');
        navigate('/mfa-setup', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } else {
      const returnUrl = resolveReturnUrl();
      if (returnUrl) {
        window.location.href = returnUrl;
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [navigate, applyTokens]);

  return null;
}
